import {useEffect, useRef} from 'react';

interface CloseOnEscapeParams {
  isOpen: boolean;
  onClose: () => void;
}

/** Body classes set while a modal is open: react-modal's, and the legacy openDialogV2 dialogs' (core.js). */
const MODAL_OPEN_BODY_CLASSES = ['ReactModal__Body--open', '-open-modal'];

/** Open popups in the order they opened; only the last one answers Escape, so one press closes one layer. */
const openPopupStack: symbol[] = [];

/**
 * Native-menu Escape semantics for an open popup: Escape closes the topmost popup and nothing else.
 *
 * Outside text fields the listener runs on window in the capture phase and stops the event there, so the
 * editor's document-level Escape shortcut (deselect) never sees the key press that dismissed the popup.
 * Inside a text field the field gets Escape first (cancel a rename, exit Fabric text editing) and the popup
 * only closes if the key bubbles up to window unhandled. While a modal is open the key is left to react-modal.
 */
export function useCloseOnEscape({isOpen, onClose}: CloseOnEscapeParams): void {
  // Kept in a ref so the listener subscribes once per open, not on every render of the caller.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const popupToken = Symbol('open-popup');
    openPopupStack.push(popupToken);

    const closeIfTopmostPopup = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || !isTopmostOpenPopup(popupToken) || isModalOpen()) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      onCloseRef.current();
    };
    const closeOnEscapeOutsideTextField = (event: KeyboardEvent): void => {
      if (!isTextFieldTarget(event.target)) {
        closeIfTopmostPopup(event);
      }
    };
    const closeOnUnhandledEscapeInTextField = (event: KeyboardEvent): void => {
      if (isTextFieldTarget(event.target) && !event.defaultPrevented) {
        closeIfTopmostPopup(event);
      }
    };

    window.addEventListener('keydown', closeOnEscapeOutsideTextField, {capture: true});
    window.addEventListener('keydown', closeOnUnhandledEscapeInTextField);
    return (): void => {
      window.removeEventListener('keydown', closeOnEscapeOutsideTextField, {capture: true});
      window.removeEventListener('keydown', closeOnUnhandledEscapeInTextField);
      openPopupStack.splice(openPopupStack.indexOf(popupToken), 1);
    };
  }, [isOpen]);
}

const isTopmostOpenPopup = (popupToken: symbol): boolean => {
  return openPopupStack[openPopupStack.length - 1] === popupToken;
};

const isModalOpen = (): boolean => {
  return MODAL_OPEN_BODY_CLASSES.some((modalOpenClass) => document.body.classList.contains(modalOpenClass));
};

const isTextFieldTarget = (target: EventTarget | null): boolean => {
  return target instanceof HTMLElement && (target.isContentEditable || target.matches('input, textarea, select'));
};
